/**
 * voice.js - what Pip says in Isaac No Youtube.
 *
 * The four pools are the PLATFORM's (../../shared/voice.js). Section 8.5 asks
 * for 100+ unique praise phrases; a per-GAME hundred is a rule nobody can keep
 * at a hundred games, and it is the CHILD who must not hear a line twice. So
 * this file adds only the lines that would be nonsense in another game, and
 * merges.
 *
 * ---------------------------------------------------------------------------
 * THE PRAISE NEVER MENTIONS WHAT THE CHILD DID NOT DO
 *
 * There is exactly one line in this file about the screen - "The screen can
 * wait." - and it is praise for something that happened, not a comment on
 * something that did not. No line here says "instead of", "not the tablet",
 * "well done for looking away". A child praised for an absence learns that the
 * absence is the interesting thing, which is the opposite of what this game is
 * for, and it also breaks section 2.4's rule that nothing in a game may feel
 * like a verdict on the child.
 *
 * ---------------------------------------------------------------------------
 * FOUR POOLS, FOUR DIFFERENT TRUTHS
 *
 * PRAISE answers a correct production. ENCOURAGEMENT answers a sound with no
 * word in it - which at Phase 1 is component B succeeding and is a real
 * achievement, not a miss. NEARLY answers a different word. ASSIST is what Pip
 * says when the tries are used up and the two of them do it together, at which
 * point nobody has failed. Mixing them teaches a child that praise means
 * nothing.
 *
 * ---------------------------------------------------------------------------
 * THE PROMPT AT PHASE 1 CONTAINS THE ANSWER, ON PURPOSE
 *
 * Section 8.2's Phase 1 mustInclude is: Pip says "Say [WORD]". The phase has
 * one support rung and it is `full_model`; the child is learning that a voice
 * does something, and errorless learning means the answer is always available.
 * So every line in PROMPTS[1].full_model carries {word} and a conformance check
 * asserts it. That is the phase, not a choice - and it is the one place in this
 * repository where a prompt is SUPPOSED to hand the answer over.
 */

import * as SHARED from '../../shared/voice.js';
import { mergePools } from '../../shared/voice.js';

/** @typedef {{text:string, tone:'bright'|'calm'}} Line */

/** The child produced the target. */
export const PRAISE = mergePools(SHARED.PRAISE, [
  { text: 'Your voice brought them over.', tone: 'bright' },
  { text: 'They heard you and turned round.', tone: 'bright' },
  { text: 'They came straight to you.', tone: 'bright' },
  { text: 'You called, and they came.', tone: 'bright' },
  { text: 'The room got warmer.', tone: 'calm' },
  { text: 'Your people are here.', tone: 'calm' },
  { text: 'That is who Isaac wanted.', tone: 'calm' },
  { text: 'The screen can wait.', tone: 'calm' },
]);

/**
 * A sound, and no word in it. Phase 1 component B, succeeding.
 *
 * The only pool allowed to answer a trial with no transcript at all.
 */
export const ENCOURAGEMENT = mergePools(SHARED.ENCOURAGEMENT, [
  { text: 'They are still here. Take your time.', tone: 'calm' },
  { text: 'Pip heard that. Now say who it is.', tone: 'calm' },
]);

/** A word came back, but not the one Pip asked for. Never "wrong". */
export const NEARLY = mergePools(SHARED.NEARLY, [
  { text: 'Somebody else. Listen to Pip once more.', tone: 'calm' },
]);

/** Tries are used up. Pip does it with them; nobody has failed. */
export const ASSIST = mergePools(SHARED.ASSIST, [
  { text: 'Pip will call them this time.', tone: 'calm' },
]);

/**
 * The prompt, by phase and by support rung.
 *
 * Rung ids are SUPPORT_LADDER values from the engine, and only rungs inside the
 * phase's bounds may appear. Phase 1's bounds are 0..0, so `full_model` is the
 * only key there will ever be here.
 *
 * Placeholders: {word} {clue}
 */
export const PROMPTS = {
  1: {
    full_model: [
      'Say {word}.',
      'Your turn. Say {word}.',
      'Ready? Say {word}.',
      'Look up here. Say {word}.',
      'Listen. {word}. Now you say {word}.',
      'Have a go. Say {word}.',
      'Say it with Pip. {word}.',
      'Together now. Say {word}.',
      'Pip will start. {word}. Say {word}.',
    ],
  },
};

/**
 * Pip's clue, spoken after a wait.
 *
 * At Phase 1 the prompt has ALREADY said the word, so a clue here is not a
 * retrieval cue - the answer was never withheld. What it is for is bringing the
 * child's attention back to the card after a silence, which is exactly the
 * difficulty this game is about. So every line points at the picture.
 */
export const HINTS = {
  1: [
    'Look at the card. {clue}',
    'Here they are. {clue}',
    'Still here, waiting. {clue}',
  ],
};

/** The generic opening for the phase. A level's own `opening` is preferred. */
export const LEVEL_INTRO = {
  1: [
    'Pip will say a name. You say it back, and they come.',
    'Somebody is here for Isaac. Say who, and they come over.',
    'Listen to Pip, then say it back.',
  ],
};

/** Between rounds, so a level never sounds like a list. */
export const ROUND_LINKS = [
  'Somebody else is here.',
  'Look who came.',
  'And this one.',
  'Turn round.',
  'Over here now.',
  'Another one came in.',
];

/** The end of a level. */
export const LEVEL_COMPLETE = [
  'Everybody is here now.',
  'The room is full of your people.',
  'That is all of them.',
  'Nobody is missing.',
];

/** The end of a session, or of the game's content. */
export const SESSION_END = [
  'Thank you for talking with Pip.',
  'Isaac had a good day. Come back soon.',
  'That was a lot of names. Well done.',
];

/**
 * Fill {placeholders}. An unknown one is left visible rather than blanked, so a
 * missing variable shows up in testing instead of producing "Say ." to a child.
 *
 * @param {string} template
 * @param {Object<string,string>} vars
 * @returns {string}
 */
export function renderLine(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (whole, key) =>
    (vars[key] === undefined || vars[key] === null ? whole : String(vars[key])));
}

export default {
  PRAISE, ENCOURAGEMENT, NEARLY, ASSIST,
  PROMPTS, HINTS, LEVEL_INTRO, ROUND_LINKS, LEVEL_COMPLETE, SESSION_END, renderLine,
};
